// src/pages/Products.jsx
import React, { useState, useEffect, useCallback } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import components
import TopHeader from '../components/dashboard/TopHeader/TopHeader';
import Sidebar from '../components/Global/sidebar'; // ✅ Import Reusable Sidebar
import { authenticatedFetch, clearStoredCredentials } from '../utils/api';

// Import styles
import '../styles/sidebar.css';
import '../styles/products.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:7018';

function Products() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ API State
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ Session helpers - Match AgentManagement.jsx pattern
  const getSession = () => {
    try {
      return JSON.parse(localStorage.getItem('userSession') || '{}');
    } catch {
      return {};
    }
  };

  // ✅ Check for companyId/sessionId (integers) like AgentManagement
  const isAuthenticated = () => {
    const session = getSession();
    return !!(session.companyId || session.sessionId);
  };

  // ✅ Fetch Products - Handle BOTH response formats (success: true OR status: 'success')
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authenticatedFetch(
        `${API_BASE}/Products/products`,
        'POST',
        {}
      );
      
      // ✅ Check for success: true (boolean) OR status: 'success' (string)
      const isSuccess = response?.success === true || response?.status === 'success';
      
      if (isSuccess) {
        const apiData = response.data || response.result || [];
        
        // ✅ Map backend response to frontend table format
        const formattedProducts = apiData.map((item) => ({
          id: item.id || item.Id,
          name: item.name || item.Name,
          category: item.categoryName || item.CategoryName || 'Uncategorized',
          provider: item.providerName || item.ProviderName || 'Unknown',
          customers: 0,
          dateAdded: item.createdAt || item.CreatedAt || new Date().toLocaleDateString('en-GB', { day: 'ordinal', month: 'short', year: 'numeric' }),
          icon: getIconForCategory(item.categoryName || item.CategoryName),
          raw: {
            categoryId: item.categoryId || item.CategoryId,
            providerId: item.providerId || item.ProviderId,
            minimumPremium: item.minimumPremium || item.MinimumPremium,
            familyPremium: item.familyPremium || item.FamilyPremium, 
            status: item.status || item.Status,
            policyDocument: item.policyDocument || item.PolicyDocument,
            benefits: item.benefits || item.Benefits,
            howItWorks: item.howItWorks || item.HowItWorks,
            howToClaim: item.howToClaim || item.HowToClaim
          }
        }));
        
        setProducts(formattedProducts);
        toast.success('Products loaded successfully');
        
      } else if (response?.status === 'fail' || response?.status === 'error' || response?.success === false) {
        // ✅ Handle session errors like AgentManagement
        const msg = (response?.message || '').toLowerCase();
        if (msg.includes('expired') || msg.includes('not found') || msg.includes('unauthorized')) {
          clearStoredCredentials();
          localStorage.removeItem('userSession');
          toast.warning(response.message || 'Session expired');
          navigate('/login', { replace: true });
          return;
        }
        throw new Error(response.message || 'Failed to fetch products');
        
      } else {
        console.warn('⚠️ Unexpected API response:', response);
        throw new Error('Invalid API response format');
      }
      
    } catch (err) {
      console.error('💥 Products fetch error:', err);
      
      const errorMsg = err.message?.toLowerCase() || '';
      
      if (errorMsg.includes('failed to fetch') || err.name === 'TypeError') {
        setError('Cannot connect to server. Check your internet.');
        toast.error('Cannot connect to server. Check your internet.');
      } else if (errorMsg.includes('401') || errorMsg.includes('expired') || errorMsg.includes('unauthorized')) {
        clearStoredCredentials();
        localStorage.removeItem('userSession');
        toast.warning('Session expired. Please log in again.');
        navigate('/login', { replace: true });
      } else if (errorMsg.includes('404')) {
        setError('API endpoint not found. Check backend configuration.');
        toast.error('API endpoint not found. Check backend configuration.');
      } else {
        setError(err.message || 'Could not load products');
        toast.error(err.message || 'Could not load products');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // ✅ Helper: Get icon based on category
  const getIconForCategory = (categoryName) => {
    const name = (categoryName || '').toLowerCase();
    if (name.includes('health') || name.includes('hospital')) return 'fa-hospital';
    if (name.includes('life')) return 'fa-shield-alt';
    if (name.includes('artisan') || name.includes('business')) return 'fa-briefcase';
    if (name.includes('accident')) return 'fa-car-crash';
    return 'fa-box';
  };

  // ✅ Simplified useEffect - let API handle auth errors
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    fetchProducts();
  }, [fetchProducts]);

  // ✅ Filter products
  const filteredProducts = products.filter(product =>
    product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.provider?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewProduct = (productId) => {
    const product = products.find(p => p.id === productId);
    navigate(`/products/${productId}`, { state: { product } });
  };

  const handleNavClick = (path, e) => {
    e?.preventDefault();
    if (path) navigate(path);
  };

  // ✅ Handle logout
  const handleLogout = (e) => {
    e?.preventDefault();
    clearStoredCredentials();
    localStorage.removeItem('userSession');
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  };

  // ✅ Calculate stats
  const totalProducts = products.length;
  const activeProducts = products.filter(p => p.raw?.status === 'Active').length;

  return (
    <div className="products-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      
      {/* ✅ Left Sidebar (Using Reusable Component) */}
      <Sidebar 
        sidebarOpen={sidebarOpen} 
        onNavClick={handleNavClick} 
        onLogout={handleLogout} 
      />

      {/* Main Content */}
      <div className="main-content">
        <TopHeader 
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          pageTitle="Plans"
          pageIcon="fa-box"
        />

        <div className="content-wrapper">
          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-6 mb-3">
              <div className="summary-card" style={{ backgroundColor: '#f0fdf4' }} data-aos="fade-up">
                <div className="card-body p-4">
                  <p className="text-muted small mb-1">Total Plans</p>
                  <h3 className="mb-0" style={{ color: '#10b981', fontWeight: '700' }}>
                    {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : totalProducts}
                  </h3>
                </div>
              </div>
            </div>
            <div className="col-md-6 mb-3">
              <div className="summary-card" style={{ backgroundColor: '#f5f3ff' }} data-aos="fade-up" data-aos-delay="100">
                <div className="card-body p-4">
                  <p className="text-muted small mb-1">Active Plans</p>
                  <h3 className="mb-0" style={{ color: '#8b5cf6', fontWeight: '700' }}>
                    {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : activeProducts}
                  </h3>
                </div>
              </div>
            </div>
          </div>

          {/* My Products Section */}
          <div className="card border-0 shadow-sm mb-4" data-aos="fade-up">
            <div className="card-body p-4">
              {/* Section Header */}
              <div className="section-header">
                <div className="section-title">
                  <h5 className="mb-1">My Plans</h5>
                  <p className="mb-0">{loading ? 'Loading...' : `${filteredProducts.length} products`}</p>
                </div>
                <div>
                  <a href="/" className="explore-link">
                    <i className="fas fa-search me-2"></i>Explore More Plans
                  </a>
                </div>
              </div>

              {/* Toolbar */}
              <div className="toolbar">
                <div className="search-box">
                  <i className="fas fa-search"></i>
                  <input 
                    type="text" 
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div className="d-flex gap-2">
                  <button className="toolbar-btn" disabled={loading}>
                    <i className="fas fa-edit me-2"></i>Edit Columns
                  </button>
                  <button className="toolbar-btn" disabled={loading}>
                    <i className="fas fa-filter me-2"></i>Filter
                  </button>
                  <button className="toolbar-btn toolbar-btn-export" disabled={loading}>
                    <i className="fas fa-download me-2"></i>Export
                  </button>
                </div>
              </div>

              {/* Products Table */}
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading plan...</p>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger" role="alert">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                    <button className="btn btn-sm btn-outline-danger ms-3" onClick={fetchProducts}>Retry</button>
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-box-open fa-4x mb-3 opacity-25"></i>
                    <h5>No products found</h5>
                    <p className="mb-0">{products.length === 0 ? 'No products available yet.' : 'Try a different search term.'}</p>
                  </div>
                ) : (
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>S/N</th>
                        <th>Plan Name</th>
                        <th>Plan Category</th>
                        <th>Provider</th>
                        <th>Min Premium</th> 
                        <th>Family Premium</th> 
                        <th>Date Added</th>
                        <th>View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product, index) => (
                        <tr key={product.id}>
                          <td>{index + 1}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="product-icon-box">
                                <i className={`fas ${product.icon}`}></i>
                              </div>
                              <div>
                                <div className="fw-semibold">{product.name}</div>
                                <small className="text-muted">{product.category}</small>
                              </div>
                            </div>
                          </td>
                          <td>{product.category}</td>
                          <td style={{ fontSize: '13px' }}>{product.provider}</td>
                          
                          {/* ✅ Min Premium Column */}
                          <td style={{ fontSize: '13px', fontWeight: '600', color: '#10b981' }}>
                            {product.raw?.minimumPremium ? `₦${Number(product.raw.minimumPremium).toLocaleString()}` : 'N/A'}
                          </td>
                          
                          {/* ✅ Family Premium Column */}
                          <td style={{ fontSize: '13px', fontWeight: '600', color: '#8b5cf6' }}>
                            {product.raw?.familyPremium ? `₦${Number(product.raw.familyPremium).toLocaleString()}` : 'N/A'}
                          </td>

                          <td style={{ fontSize: '13px' }}>{product.dateAdded}</td>
                          <td>
                            <button 
                              className="btn-view"
                              onClick={() => handleViewProduct(product.id)}
                              title={`View details for ${product.name}`}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div className="text-muted small">
                  Showing <strong>1-{Math.min(5, filteredProducts.length)}</strong> of <strong>{filteredProducts.length}</strong> Plans
                </div>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className="page-item disabled">
                      <a className="page-link" href="/">
                        <i className="fas fa-angle-left"></i>
                      </a>
                    </li>
                    <li className="page-item active">
                      <a className="page-link" href="/">1</a>
                    </li>
                    <li className="page-item">
                      <a className="page-link" href="/">2</a>
                    </li>
                    <li className="page-item">
                      <a className="page-link" href="/">3</a>
                    </li>
                    <li className="page-item">
                      <a className="page-link" href="/">
                        <i className="fas fa-angle-right"></i>
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS (Sidebar styles removed since they are in Sidebar.css now) */}
      <style>{`
        .products-page { display: flex; min-height: 100vh; background-color: #f8f9fa; }
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; }
        .product-icon-box { width: 36px; height: 36px; border-radius: 8px; background: #fef3e2; color: #F89423; display: flex; align-items: center; justify-content: center; margin-right: 12px; font-size: 14px; }
        .btn-view { background: none; border: none; color: #F89423; font-weight: 500; cursor: pointer; padding: 4px 12px; border-radius: 4px; transition: background 0.2s; }
        .btn-view:hover { background: #fef3e2; }
        .toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 12px; }
        .search-box { position: relative; flex: 1; max-width: 300px; }
        .search-box i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #9ca3af; }
        .search-box input { width: 100%; padding: 10px 14px 10px 40px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; outline: none; transition: border-color 0.2s; }
        .search-box input:focus { border-color: #F89423; }
        .toolbar-btn { padding: 8px 16px; border: 1px solid #d1d5db; background: white; border-radius: 8px; color: #6b7280; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; }
        .toolbar-btn:hover:not(:disabled) { background: #f9fafb; color: #374151; }
        .toolbar-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .toolbar-btn-export { color: #F89423; background: #fef3e2; border-color: #fcd34d; }
        .toolbar-btn-export:hover:not(:disabled) { background: #fde68a; }
        .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 12px; }
        .explore-link { color: #F89423; text-decoration: none; font-weight: 500; font-size: 14px; display: flex; align-items: center; }
        .explore-link:hover { text-decoration: underline; }
        
        @media (max-width: 992px) { 
          .main-content { margin-left: 0; } 
        }
      `}</style>
    </div>
  );
}

export default Products;